import { useEffect, useState } from "react";
import { ArrowUpRight, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import CasesSectionHead from "./CasesSectionHead";
import type { MediaItem } from "./CasesContent";

type CaseMediaProps = {
  id: string;
  title: string;
  text: string;
  items: MediaItem[];
};

// tempo de cada matéria em destaque antes de passar para a próxima
const INTERVAL = 7000;

const domain = (href: string) => new URL(href).hostname.replace(/^www\./, "");

// Calriz na mídia: uma matéria em destaque à esquerda (print largo numa janela de navegador, veículo, data, chamada
// original e link) e as outras em miniatura à direita, na ordem em que vão entrar. A cada INTERVAL a primeira
// miniatura sobe para o destaque e a que estava lá vai para o fim da fila; clicar numa miniatura traz ela na hora.
// A troca não pausa com o mouse em cima.
export default function CaseMedia({ id, title, text, items }: CaseMediaProps) {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState(0);

  const formatDate = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" });

  // `active` nas dependências: trocar à mão reinicia a contagem
  useEffect(() => {
    if (items.length < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setTimeout(() => setActive((i) => (i + 1) % items.length), INTERVAL);
    return () => window.clearTimeout(timer);
  }, [active, items.length]);

  if (!items.length) return null;

  const current = items[active];
  // fila das miniaturas: começa pela próxima a entrar
  const queue = items.map((_, n) => (active + 1 + n) % items.length).slice(0, -1);

  return (
    <section className="cz-cases-section" id={id} aria-labelledby={`${id}-title`}>
      <div className="cz-cases-section__inner">
        <CasesSectionHead
          id={`${id}-title`}
          eyebrow={t("cases_page.labels.media_eyebrow", "Na mídia")}
          title={title}
          text={text}
        />
        <div className="cz-media">
          {/* key: remonta a cada troca para refazer a animação de entrada e a barra de tempo */}
          <article key={current.key} className="cz-media-main" aria-live="polite">
            {/* o print também abre a matéria; fica fora do tab porque o link do rodapé leva ao mesmo lugar */}
            <a href={current.href} target="_blank" rel="noopener noreferrer" className="cz-media-main__window" tabIndex={-1}>
              <span className="cz-media-main__bar" aria-hidden="true">
                <span className="cz-media-main__dots">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="cz-media-main__address">
                  <Lock size={11} strokeWidth={2.2} />
                  {domain(current.href)}
                </span>
              </span>
              <span className="cz-media-main__media">
                <img src={current.image} alt={current.imgAlt} className="cz-media-main__img" />
              </span>
              {items.length > 1 && (
                <span className="cz-media-main__progress" aria-hidden="true" style={{ animationDuration: `${INTERVAL}ms` }} />
              )}
            </a>
            <div className="cz-media-main__body">
              <p className="cz-media__meta">
                <span className="cz-media__source">{current.source}</span>
                <time dateTime={current.date}>{formatDate(current.date)}</time>
              </p>
              <h3 className="cz-media-main__headline">{current.headline}</h3>
              <p className="cz-media-main__text">{current.text}</p>
              <a href={current.href} target="_blank" rel="noopener noreferrer" className="cz-media-main__link">
                {t("cases_page.labels.media_read", "Ler a matéria")}
                <ArrowUpRight size={16} aria-hidden="true" />
              </a>
            </div>
          </article>

          <ul className="cz-media-thumbs">
            {queue.map((i) => {
              const item = items[i];
              return (
                <li key={item.key} className="cz-media-thumbs__item">
                  <button type="button" className="cz-media-thumb" onClick={() => setActive(i)}>
                    <span className="cz-media-thumb__media">
                      <img src={item.image} alt="" className="cz-media-thumb__img" loading="lazy" />
                    </span>
                    <span className="cz-media-thumb__body">
                      <span className="cz-media__source">{item.source}</span>
                      <span className="cz-media-thumb__headline">{item.headline}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
